import { useEffect, useState } from 'react'
import {
  AddCircleOutlineIcon,
  ArrowLeftIcon,
  Button,
  DangerousIcon,
  DrawerStepper,
  showToast,
  Text,
} from '@mercai/clever'

import { DrawerSupplierProps } from './drawer-supplier.type'
import {
  schemaSelectSupplier,
  schemaSupplier,
  SelectSupplierForm,
  SupplierForm,
} from '@/components'
import { SupplierProps } from '@/types'
import { useValidationErrors } from '@/helpers'
import { useTranslation } from 'react-i18next'

import styles from './drawer-supplier.module.css'
import { useCreateSupplier } from '@/services'

export const DrawerSupplier = ({
  isOpen,
  onClose,
  supplier,
  setSupplier,
}: DrawerSupplierProps) => {
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const [selectedSupplierValues, setSelectedSupplierValues] =
    useState<SupplierProps>()

  const [supplierFormValues, setSupplierFormValues] = useState<
    Partial<SupplierProps>
  >({})

  const {
    errors: supplierFormErrors,
    isValid: isValidSupplier,
    onHandleListenErrors: onHandleListenErrorsSupplier,
  } = useValidationErrors(schemaSupplier(t), {
    ...supplierFormValues,
  })

  const {
    errors: selectSupplierFormErrors,
    isValid: isValidSelectSupplier,
    onHandleListenErrors: onHandleListenErrorsSelectSupplier,
  } = useValidationErrors(schemaSelectSupplier(t), {
    supplier: selectedSupplierValues,
  })

  const { mutateAsync, isPending } = useCreateSupplier()

  const handleClose = () => {
    onHandleListenErrorsSelectSupplier(false)
    onHandleListenErrorsSupplier(false)

    onClose()
  }

  const onHandleSubmitSelectSupplier = () => {
    if (!isValidSelectSupplier) {
      return onHandleListenErrorsSelectSupplier(true)
    }

    onHandleListenErrorsSelectSupplier(false)

    if (selectedSupplierValues) setSupplier(selectedSupplierValues)

    handleClose()
  }

  const onBackSupplierForm = (onNavigateToPreviousStep: () => void) => {
    onHandleListenErrorsSupplier(false)

    onNavigateToPreviousStep()
  }

  const onHandleSubmitSupplier = async (
    onNavigateToPreviousStep: () => void,
  ) => {
    if (!isValidSupplier) {
      return onHandleListenErrorsSupplier(true)
    }

    onHandleListenErrorsSupplier(false)

    const response = await mutateAsync(supplierFormValues)

    if (response.success && response.data) {
      setSelectedSupplierValues(response.data)
      onNavigateToPreviousStep()
    } else {
      showToast({
        variant: 'error',
        title: t('componentDrawerSupplierSupplierCreatedErrorMessage'),
        icon: <DangerousIcon />,
      })
    }
  }

  useEffect(() => {
    setSelectedSupplierValues(supplier)
  }, [supplier])

  return (
    <DrawerStepper
      isOpen={isOpen}
      onHandleOpen={handleClose}
      initialStep="root"
      translations={{ optional: '' }}
      title={t('componentDrawerSupplierTitle')}
      description={t('componentDrawerSupplierDescription')}
      steps={{
        root: {
          backNavigate: 'root',
          isOptional: false,
          component: ({ onNavigateToNextStep }) => (
            <div>
              <div className={styles['new-supplier-box']}>
                <Text variant="title-medium">
                  {t('componentDrawerSupplierSelectSupplierTitle')}
                </Text>
                <Button
                  variant="success-light"
                  onClick={() => onNavigateToNextStep('add_supplier')}
                  size="small"
                >
                  {t('componentDrawerSupplierSelectSupplierAddButton')}
                  <AddCircleOutlineIcon />
                </Button>
              </div>
              <SelectSupplierForm
                values={selectedSupplierValues}
                onChangeValues={setSelectedSupplierValues}
                errors={selectSupplierFormErrors}
              />
            </div>
          ),
          footer: (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={handleClose}>
                {t('componentDrawerSupplierCancelButton')}
              </Button>
              <Button variant="primary" onClick={onHandleSubmitSelectSupplier}>
                {t('componentDrawerSupplierConfirmButton')}
              </Button>
            </div>
          ),
        },
        add_supplier: {
          backNavigate: 'root',
          isOptional: true,
          component: (
            <SupplierForm
              values={supplierFormValues}
              onChangeValues={setSupplierFormValues}
              errors={supplierFormErrors}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <div className={styles.footer}>
              <Button variant="secondary" onClick={handleClose}>
                {t('componentDrawerSupplierCancelButton')}
              </Button>
              <div className={styles.row}>
                <Button
                  variant="success-light"
                  onClick={() => onBackSupplierForm(onNavigateToPreviousStep)}
                >
                  <ArrowLeftIcon />
                  {t('componentDrawerSupplierBackButton')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    onHandleSubmitSupplier(onNavigateToPreviousStep)
                  }
                  isActive={!isPending}
                  disabled={isPending}
                >
                  {t('componentDrawerSupplierConfirmButton')}
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  )
}
